import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api.js';

function emptyForm() {
  return {
    code: '', name: '', brand: '', location: '', isGroup: false, parentGroupId: '', active: true,
    addressFull: '', phone: '', fax: '', payableEmail: '', taxTPS: '', taxTVQ: '',
  };
}

const ROLES = ['VIEWER', 'CREATOR', 'APPROVER', 'MANAGER'];

function AccessManager({ concession, users, onChanged }) {
  const { t } = useTranslation();
  const [access, setAccess] = useState([]);
  const [newUserId, setNewUserId] = useState('');
  const [newRole, setNewRole] = useState('VIEWER');

  const reload = () => {
    api.get(`/api/access?concessionId=${concession.id}`)
      .then((res) => setAccess(res.data.access))
      .catch((err) => toast.error(err.message));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [concession.id]);

  const grant = async () => {
    if (!newUserId) return;
    try {
      await api.post('/api/access', { userId: newUserId, concessionId: concession.id, role: newRole });
      setNewUserId(''); setNewRole('VIEWER');
      reload(); onChanged?.();
    } catch (err) { toast.error(err.message); }
  };

  const revoke = async (id) => {
    try {
      await api.del(`/api/access/${id}`);
      reload(); onChanged?.();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="mt-3 p-3 bg-zinc-900/30 rounded">
      <div className="text-xs text-zinc-400 mb-2">{t('access.title')}</div>
      <ul className="space-y-1 mb-2">
        {access.map((a) => (
          <li key={a.id} className="flex items-center justify-between text-xs bg-zinc-900/50 rounded px-2 py-1">
            <span>{a.user.name} <span className="text-zinc-500">· {a.user.email}</span></span>
            <span className="text-zinc-400 mr-2">{a.role}</span>
            <button onClick={() => revoke(a.id)} className="text-rose-400 hover:text-rose-300">×</button>
          </li>
        ))}
        {access.length === 0 && <li className="text-xs text-zinc-500">{t('access.empty')}</li>}
      </ul>
      <div className="flex gap-2">
        <select value={newUserId} onChange={(e) => setNewUserId(e.target.value)} className="input text-xs">
          <option value="">{t('access.selectUser')}</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="input text-xs max-w-[120px]">
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={grant} className="btn-primary text-xs">{t('access.grant')}</button>
      </div>
    </div>
  );
}

export default function ConcessionsAdmin() {
  const { t } = useTranslation();
  const [concessions, setConcessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [expandedId, setExpandedId] = useState(null);

  const reload = () => {
    api.get('/api/concessions').then((res) => setConcessions(res.data.concessions)).catch((err) => toast.error(err.message));
  };
  useEffect(() => {
    reload();
    api.get('/api/users').then((res) => setUsers(res.data.users)).catch(() => {});
  }, []);

  const startNew = () => { setEditing('new'); setForm(emptyForm()); };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, name: c.name,
      brand: c.brand || '', location: c.location || '',
      isGroup: !!c.isGroup, parentGroupId: c.parentGroupId || '',
      active: c.active,
      addressFull: c.addressFull || '', phone: c.phone || '', fax: c.fax || '',
      payableEmail: c.payableEmail || '', taxTPS: c.taxTPS || '', taxTVQ: c.taxTVQ || '',
    });
  };
  const cancel = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        brand: form.brand || null,
        location: form.location || null,
        parentGroupId: form.parentGroupId || null,
        addressFull: form.addressFull || null,
        phone: form.phone || null,
        fax: form.fax || null,
        payableEmail: form.payableEmail || null,
        taxTPS: form.taxTPS || null,
        taxTVQ: form.taxTVQ || null,
      };
      if (editing === 'new') {
        await api.post('/api/concessions', payload);
        toast.success(t('concessions.created'));
      } else {
        await api.put(`/api/concessions/${editing.id}`, payload);
        toast.success(t('concessions.updated'));
      }
      cancel(); reload();
    } catch (err) { toast.error(err.message); }
  };

  const groups = concessions.filter((c) => c.isGroup);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">{t('concessions.title')}</h1>
        <button onClick={startNew} className="btn-primary">{t('concessions.new')}</button>
      </div>
      {editing && (
        <form onSubmit={submit} className="card p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">{t('concessions.form.code')}</label>
              <input required className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="STPC" /></div>
            <div><label className="label">{t('concessions.form.name')}</label>
              <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Spinelli Toyota Pointe-Claire" /></div>
            <div><label className="label">{t('concessions.form.brand')}</label>
              <input className="input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Toyota" /></div>
            <div><label className="label">{t('concessions.form.location')}</label>
              <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Pointe-Claire" /></div>
            <div className="col-span-2 flex items-center gap-6">
              <label className="text-xs text-zinc-400 flex items-center gap-2">
                <input type="checkbox" checked={form.isGroup} onChange={(e) => setForm({ ...form, isGroup: e.target.checked })} />
                {t('concessions.form.isGroup')}
              </label>
              <label className="text-xs text-zinc-400 flex items-center gap-2">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                {t('common.active')}
              </label>
              {!form.isGroup && (
                <div>
                  <label className="label inline-block mr-2">{t('concessions.form.parentGroup')}</label>
                  <select value={form.parentGroupId} onChange={(e) => setForm({ ...form, parentGroupId: e.target.value })} className="input inline-block w-auto">
                    <option value="">—</option>
                    {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="pt-2 mt-1 border-t border-zinc-800">
            <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">{t('concessions.billing')}</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="label">{t('concessions.form.addressFull')}</label>
                <input className="input" value={form.addressFull} onChange={(e) => setForm({ ...form, addressFull: e.target.value })} /></div>
              <div><label className="label">{t('concessions.form.phone')}</label>
                <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="label">{t('concessions.form.fax')}</label>
                <input className="input" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} /></div>
              <div className="col-span-2"><label className="label">{t('concessions.form.payableEmail')}</label>
                <input type="email" className="input" value={form.payableEmail} onChange={(e) => setForm({ ...form, payableEmail: e.target.value })} /></div>
              <div><label className="label">{t('concessions.form.taxTPS')}</label>
                <input className="input" value={form.taxTPS} onChange={(e) => setForm({ ...form, taxTPS: e.target.value })} /></div>
              <div><label className="label">{t('concessions.form.taxTVQ')}</label>
                <input className="input" value={form.taxTVQ} onChange={(e) => setForm({ ...form, taxTVQ: e.target.value })} /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} className="btn-ghost">{t('common.cancel')}</button>
            <button type="submit" className="btn-primary">{t('common.save')}</button>
          </div>
        </form>
      )}
      <ul className="space-y-2">
        {concessions.map((c) => (
          <li key={c.id} className="card p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">{c.code}</span>
                  <span className="font-medium">{c.name}</span>
                  {c.isGroup && <span className="text-[10px] uppercase text-cyan-400">group</span>}
                  {!c.active && <span className="text-[10px] uppercase text-zinc-600">inactive</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {[c.brand, c.location].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div className="flex gap-1 text-xs">
                <button onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} className="btn-ghost text-xs">{t('access.manage')}</button>
                <button onClick={() => startEdit(c)} className="btn-ghost text-xs">{t('common.edit')}</button>
              </div>
            </div>
            {expandedId === c.id && <AccessManager concession={c} users={users} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
